import { makeAutoObservable } from 'mobx';
import { Order, PdfContract, QuickContract } from '../../models/OrderContract';
import { BuyerInfoDTO } from '../../models/Buyer';
import { OrderContractApi } from '../../apis/OrderContractApi'; 
import { SellerApi } from '../../apis/SellerApi';
import { SellerInfo } from '../../models/Seller';

export interface OrderWithSellerInfo extends Order {
  sellerInfo?: SellerInfo;
}

export class OrderContractModel {
  orders: OrderWithSellerInfo[] = [];
  /** null 表示全部状态 */
  statusFilter: number | null = null;
  page = 1;
  pageSize = 10;
  loading = false;
  detailLoading = false;
  detailError: string | null = null;
  selectedOrder: OrderWithSellerInfo | null = null;
  pdfContracts: PdfContract[] = [];
  quickContracts: QuickContract[] = [];
  selectedPdfContract: PdfContract | null = null;
  pdfPreviewVisible = false;
  quickAppGuideVisible = false;
  selectedQuickContract: QuickContract | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  // 获取订单列表
  async fetchOrders() {
    this.loading = true;
    try {
      // 获取最近30天的订单
      const afterTime = Date.now() - 30 * 24 * 60 * 60 * 1000;
      const orders = await OrderContractApi.getOrdersAfterTime(afterTime);
      console.log("orders:", orders);
      
      // 添加空值检查
      if (!orders || !Array.isArray(orders)) {
        console.warn('获取订单列表失败：返回数据为空或不是数组');
        this.orders = [];
        return;
      }
      
      // 获取所有卖家ID
      const sellerIds = Array.from(new Set(orders.map(order => order.sellerId)));
      
      // 获取卖家信息（失败不影响订单列表展示）
      let sellerMap = new Map<string, SellerInfo>();
      try {
        const sellerInfos = await SellerApi.getSellerInfo(sellerIds);
        sellerMap = new Map(sellerInfos.map(seller => [seller.sellerId, seller]));
      } catch (error) {
        console.warn('获取卖家信息失败，订单列表仍正常展示:', error);
      }
      
      this.orders = orders.map(order => ({
        ...order,
        sellerInfo: sellerMap.get(order.sellerId)
      }));
    } catch (error) {
      console.error('获取订单列表失败:', error);
      this.orders = [];
    } finally {
      this.clampPage();
      this.loading = false;
    }
  }

  get filteredOrders(): OrderWithSellerInfo[] {
    if (this.statusFilter == null) {
      return this.orders;
    }
    return this.orders.filter(order => order.status === this.statusFilter);
  }

  get pagedOrders(): OrderWithSellerInfo[] {
    const start = (this.page - 1) * this.pageSize;
    return this.filteredOrders.slice(start, start + this.pageSize);
  }

  setStatusFilter(status: number | null) {
    this.statusFilter = status;
    this.page = 1;
  }

  setPage(page: number) {
    this.page = page;
  }

  private clampPage() {
    const maxPage = Math.max(1, Math.ceil(this.filteredOrders.length / this.pageSize));
    if (this.page > maxPage) {
      this.page = maxPage;
    }
  }

  // 获取订单详情
  async fetchOrderDetail(orderId: string) {
    this.detailLoading = true;
    this.detailError = null;
    this.selectedOrder = null;
    this.pdfContracts = [];
    this.quickContracts = [];
    try {
      const order = await OrderContractApi.getOrderDetail(orderId);
      if (order) {
        // 获取买家信息
        const sellerInfos = await SellerApi.getSellerInfo([order.sellerId]);
        // 添加空值检查
        if (sellerInfos && Array.isArray(sellerInfos) && sellerInfos.length > 0) {
          this.selectedOrder = {
            ...order,
            sellerInfo: sellerInfos[0]
          };
        } else {
          console.warn('获取卖家信息失败，设置订单时没有卖家信息');
          this.selectedOrder = {
            ...order,
            sellerInfo: undefined
          };
        }
        
        this.pdfContracts = order.pdfContractList?.length
          ? await OrderContractApi.queryPdfContractsByIds(order.pdfContractList)
          : [];

        this.quickContracts = order.quickContractList?.length
          ? await OrderContractApi.queryQuickContractsByIds(order.quickContractList)
          : [];
      } else {
        this.detailError = '订单不存在或无权访问';
      }
    } catch (error) {
      console.error('获取订单详情失败:', error);
      this.detailError = '获取订单详情失败，请稍后重试';
    } finally {
      this.detailLoading = false;
    }
  }

  // 设置选中的PDF合同
  setSelectedPdfContract(contract: PdfContract | null) {
    this.selectedPdfContract = contract;
  }

  // 显示PDF预览弹框
  showPdfPreview(contract: PdfContract) {
    this.selectedPdfContract = contract;
    this.pdfPreviewVisible = true;
  }

  // 隐藏PDF预览弹框
  hidePdfPreview() {
    this.pdfPreviewVisible = false;
    this.selectedPdfContract = null;
  }

  showQuickAppGuide(contract: QuickContract) {
    this.selectedQuickContract = contract;
    this.quickAppGuideVisible = true;
  }

  hideQuickAppGuide() {
    this.quickAppGuideVisible = false;
    this.selectedQuickContract = null;
  }

  // 添加PDF合同
  async addPdfContract(
    orderId: string,
    contractName: string,
    pdfUrl: string,
    pdfSignMode: number = 0,
  ) {
    try {
      const newContract = await OrderContractApi.addPdfContract({
        contractName,
        pdfUrl,
        orderId,
        posterType: 0,
        pdfSignMode,
      });
      
      if (newContract) {
        // 将新合同添加到当前订单的PDF合同列表中
        this.pdfContracts.push(newContract);
        
        // 如果当前选中的订单是这个订单，也更新其PDF合同列表
        if (this.selectedOrder && this.selectedOrder.orderId === orderId) {
          if (!this.selectedOrder.pdfContractList) {
            this.selectedOrder.pdfContractList = [];
          }
          this.selectedOrder.pdfContractList.push(newContract.contractId);
        }
      }
      
      return newContract;
    } catch (error) {
      console.error('添加PDF合同失败:', error);
      throw error;
    }
  }

  // 重置状态
  reset() {
    this.detailLoading = false;
    this.detailError = null;
    this.selectedOrder = null;
    this.pdfContracts = [];
    this.quickContracts = [];
    this.selectedPdfContract = null;
    this.pdfPreviewVisible = false;
    this.quickAppGuideVisible = false;
    this.selectedQuickContract = null;
  }
}

export const orderContractModel = new OrderContractModel(); 